/**
 * 系统管理 → 现金功能：替代财务中心公开展示的管理卡片。
 * 管理员 / CEO 必须输入本人登录密码；密码提交后立即清空，解锁凭据只留在本页内存。
 * 刷新、离页、换账号或五分钟到期后重新锁定；服务端同时校验，防止只藏 UI 可被绕过。
 */
import React, { useEffect, useRef, useState } from "react";
import { LockKeyhole, Loader2 } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { CashModeCard } from "../../components/CashModeCard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

/** 按账号挂载独立表单，切换账号时销毁旧解锁状态，避免凭据或详情沿用。 */
export default function CashModeSettings() {
  const { user } = useApp();
  return <CashModeForm key={user.username} />;
}

/** 无外部参数；仅在本页维护密码、错误及解锁详情，业务启停值由 AppContext 同步。 */
function CashModeForm() {
  const { api, lang, setCashMode } = useApp();
  const zh = lang === "zh";
  const [password, setPassword] = useState("");
  const [access, setAccess] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const passwordRef = useRef(null);
  const pending = useRef(null);

  useEffect(() => () => pending.current?.abort(), []);
  useEffect(() => {
    if (!access) { passwordRef.current?.focus(); return undefined; }
    // 采用服务端到期时间；后台标签页恢复时也由接口校验兜底，不延长解锁时限。
    const timer = setTimeout(() => setAccess(null), Math.max(0, access.expiresAt * 1000 - Date.now()));
    return () => clearTimeout(timer);
  }, [access]);

  /** 校验当前账号密码；失败保留登录状态并聚焦错误字段，离页则取消未完成请求。 */
  const unlock = async (event) => {
    event.preventDefault();
    if (busy || !password) return;
    setBusy(true);
    setError("");
    const controller = new AbortController();
    pending.current = controller;
    const submittedPassword = password;
    setPassword("");
    try {
      const { data } = await api.post("/admin/cash-mode/unlock", { password: submittedPassword }, { signal: controller.signal, timeout: 15000 });
      if (!controller.signal.aborted) setAccess(data);
    } catch (e) {
      if (!controller.signal.aborted) {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : (zh ? "验证失败，请重试" : "Verification failed. Try again."));
      }
    } finally {
      if (!controller.signal.aborted) { setBusy(false); passwordRef.current?.focus(); }
    }
  };

  /** 保存使用同一短期凭据；凭据失效立即收起详情，迟到响应不能重新解锁页面。 */
  const save = async (enabled) => {
    const token = access.unlockToken;
    try {
      const cashMode = await setCashMode(enabled, token);
      setAccess((current) => current?.unlockToken === token ? { ...current, cashMode } : current);
    } catch (e) {
      if ([401, 403, 423].includes(e?.response?.status)) setAccess(null);
      throw e;
    }
  };

  return (
    <div className="max-w-4xl space-y-4" data-testid="cash-mode-settings">
      <h1>{zh ? "现金功能" : "Cash Settings"}</h1>
      {access ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-500">{zh ? "已验证身份，5 分钟后自动锁定；离开本页后需重新验证。" : "Verified for 5 minutes. Leaving this page requires verification again."}</p>
            <Button variant="outline" className="min-h-11" onClick={() => setAccess(null)} data-testid="cash-mode-lock">
              <LockKeyhole aria-hidden="true" />{zh ? "锁定" : "Lock"}
            </Button>
          </div>
          <CashModeCard zh={zh} cashMode={access.cashMode} onChange={save} />
        </>
      ) : (
        <section className="max-w-lg rounded-xl border border-slate-200 bg-white p-5 sm:p-6" aria-labelledby="cash-unlock-title" data-testid="cash-mode-locked">
          <div className="mb-5 flex items-start gap-3">
            <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" aria-hidden="true" />
            <div className="space-y-1">
              <h2 id="cash-unlock-title" className="font-semibold text-slate-900">{zh ? "验证身份" : "Verify your identity"}</h2>
              <p className="text-sm text-slate-500" id="cash-unlock-hint">{zh ? "仅 IT 管理员 / CEO 可用。输入本人登录密码后查看和操作现金开关。" : "IT admin / CEO only. Enter your login password to view and manage the cash setting."}</p>
            </div>
          </div>
          <form onSubmit={unlock} className="space-y-4" aria-busy={busy}>
            <div className="space-y-2">
              <label htmlFor="cash-unlock-password" className="block text-sm font-medium text-slate-700">{zh ? "本人登录密码" : "Your login password"}</label>
              <Input ref={passwordRef} id="cash-unlock-password" data-testid="cash-mode-password" type="password" autoComplete="current-password"
                required maxLength={1024} readOnly={busy} value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }}
                aria-invalid={!!error} aria-describedby={error ? "cash-unlock-hint cash-unlock-error" : "cash-unlock-hint"} className="min-h-11" />
              {error && <p id="cash-unlock-error" role="alert" className="text-sm text-destructive">{error}</p>}
            </div>
            <Button type="submit" disabled={busy || !password} className="min-h-11" data-testid="cash-mode-unlock">
              {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <LockKeyhole aria-hidden="true" />}
              {busy ? (zh ? "正在验证…" : "Verifying…") : (zh ? "验证并解锁" : "Verify and unlock")}
            </Button>
          </form>
        </section>
      )}
    </div>
  );
}
